// src/components/borrowing/ManageDefaulter.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { reportService } from '../../services/reportService';
import { fileStorageService } from '../../services/fileStorageService';
import { generateShareLinks } from '../../services/notificationService';
import { ReportStatus, BorrowerResponseType } from '../constants';
import { MessageSquare, 
  CheckCircle, 
  Ban, 
  ArrowLeft, 
  AlertCircle, 
  Handshake, 
  CheckSquare, 
  Edit2, 
  Upload, 
  X, 
  Send,
  FileText, 
  Bell, 
  MessageCircle, 
  Mail, 
  Loader2 
} from 'lucide-react';
import ReportCard from '../lending/ReportCard';
import MessageSenderModal from '../messaging/MessageSenderModal';

const ManageDefaulter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Borrower Simulation State
  const [isSimulatingBorrower, setIsSimulatingBorrower] = useState(false);
  const [borrowerResponseText, setBorrowerResponseText] = useState('');
  const [responseType, setResponseType] = useState(null);
  const [borrowerAttachments, setBorrowerAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  // Manual Notification Modal State
  const [showResendModal, setShowResendModal] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    loadReport();
  }, [id, currentUser, navigate]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await reportService.getReport(id);
      
      if (!data) {
        navigate('/dashboard');
        return;
      }
      
      setReport(data);
    } catch (error) {
      console.error('Error loading report:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!report) return;
    
    try {
      setUpdating(true);
      await reportService.updateReportStatus(report.id, newStatus);
      setReport({ ...report, status: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleBorrowerFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) { 
        alert("File size exceeds 5MB limit.");
        return;
      }

      setUploadingFiles(true);
      try {
        const previewData = await fileStorageService.fileToBase64(file);
        const tempItem = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type: file.type,
          data: previewData,
          file: file,
          isTemp: true
        };
        setBorrowerAttachments([...borrowerAttachments, tempItem]);
      } catch (error) {
        console.error('Error processing file:', error);
        alert('Failed to process file. Please try again.');
      } finally {
        setUploadingFiles(false);
      }
    }
  };

  const removeBorrowerAttachment = (id) => {
    setBorrowerAttachments(borrowerAttachments.filter(a => a.id !== id));
  };

  const submitBorrowerResponse = async () => {
    if (!report || !borrowerResponseText.trim() || !responseType) return;
    
    try {
      setUpdating(true);

      // Upload borrower evidence files
      const uploadedFiles = [];
      for (const attachment of borrowerAttachments) {
        if (attachment.isTemp && attachment.file) {
          const result = await fileStorageService.uploadBorrowerEvidence(report.id, attachment.file);
          uploadedFiles.push({
            id: attachment.id,
            name: attachment.name,
            type: attachment.type,
            url: result.url,
            path: result.path
          });
        }
      }

      await reportService.addBorrowerResponse(report.id, {
        text: borrowerResponseText,
        type: responseType,
        attachments: uploadedFiles
      });

      // Reload report
      await loadReport();
      
      // Reset form
      setBorrowerResponseText('');
      setResponseType(null);
      setBorrowerAttachments([]);
      setIsSimulatingBorrower(false);
      
      alert('Response submitted successfully.');
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-emerald-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Report not found</p>
        </div>
      </div>
    );
  }

  const isOwner = currentUser && currentUser.uid === report.lenderId;

  // Resend Modal
  const ResendModal = () => {
    const links = generateShareLinks(report);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
        <div className="bg-slate-800 rounded-xl p-6 max-w-lg w-full border border-slate-700 relative animate-in slide-in-from-bottom-4">
          <button 
            onClick={() => setShowMessageModal(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-indigo-900/20 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-900/30 transition-colors"
          >
            <Send size={18} />
            Send Message
          </button>
          
          <h3 className="text-xl font-bold text-white mb-4">Resend Notification</h3>
          <p className="text-sm text-slate-400 mb-6">Send a reminder to the borrower using your preferred channel.</p>
          
          <div className="space-y-3">
            <a 
              href={links.waLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 rounded-lg transition-colors"
            >
              <MessageCircle size={20} /> WhatsApp
            </a>
            {report.borrowerEmail ? (
              <a 
                href={links.mailLink}
                className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-colors"
              >
                <Mail size={20} /> Email
              </a>
            ) : (
              <div className="p-3 bg-slate-700/50 rounded text-center text-slate-500 text-sm">
                Email not provided for this borrower.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {showResendModal && <ResendModal />}
      
      <button 
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-slate-400 hover:text-white mb-6 transition-colors group"
      >
        <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" /> 
        Back to Dashboard
      </button>

      <div className="mb-6 flex justify-end">
        <button 
          onClick={() => setIsSimulatingBorrower(!isSimulatingBorrower)}
          className={`text-xs px-3 py-1 rounded border transition-colors ${
            isSimulatingBorrower 
              ? 'bg-orange-500 border-orange-500 text-white' 
              : 'bg-transparent border-slate-600 text-slate-400 hover:border-slate-500'
          }`}
        >
          {isSimulatingBorrower ? 'Simulating: Borrower View' : 'Simulating: Lender View'}
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-xl font-bold text-white mb-4">Report Thread</h2>
          
          <ReportCard report={report} isPublic={true} isOwner={isOwner} onUpdate={loadReport} />

          {isSimulatingBorrower && report.borrowerResponse && (
            <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
              <CheckCircle size={16} />
              <span>Response submitted. This is visible in the thread above.</span>
            </div>
          )}

          {isSimulatingBorrower && !report.borrowerResponse && (
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg mt-6 animate-in slide-in-from-bottom-4">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <MessageSquare size={18} className="text-emerald-400"/> Reply to this Report
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Select Response Type</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <button 
                      type="button"
                      onClick={() => setResponseType(BorrowerResponseType.DISPUTE)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        responseType === BorrowerResponseType.DISPUTE 
                          ? 'bg-orange-900/20 border-orange-500 ring-1 ring-orange-500' 
                          : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle size={16} className={responseType === BorrowerResponseType.DISPUTE ? "text-orange-500" : "text-slate-500"} />
                        <span className={`text-sm font-bold ${responseType === BorrowerResponseType.DISPUTE ? "text-orange-400" : "text-slate-300"}`}>
                          Dispute
                        </span>
                      </div>
                    </button>
                    <button 
                      type="button"
                      onClick={() => setResponseType(BorrowerResponseType.SETTLEMENT_OFFER)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        responseType === BorrowerResponseType.SETTLEMENT_OFFER 
                          ? 'bg-blue-900/20 border-blue-500 ring-1 ring-blue-500' 
                          : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Handshake size={16} className={responseType === BorrowerResponseType.SETTLEMENT_OFFER ? "text-blue-500" : "text-slate-500"} />
                        <span className={`text-sm font-bold ${responseType === BorrowerResponseType.SETTLEMENT_OFFER ? "text-blue-400" : "text-slate-300"}`}>
                          Offer Settlement
                        </span>
                      </div>
                    </button>
                    <button 
                      type="button"
                      onClick={() => setResponseType(BorrowerResponseType.CLAIM_PAID)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        responseType === BorrowerResponseType.CLAIM_PAID 
                          ? 'bg-emerald-900/20 border-emerald-500 ring-1 ring-emerald-500' 
                          : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CheckSquare size={16} className={responseType === BorrowerResponseType.CLAIM_PAID ? "text-emerald-500" : "text-slate-500"} />
                        <span className={`text-sm font-bold ${responseType === BorrowerResponseType.CLAIM_PAID ? "text-emerald-400" : "text-slate-300"}`}>
                          Already Paid
                        </span>
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Your Message</label>
                  <textarea 
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none placeholder-slate-600 transition-all"
                    rows={4}
                    value={borrowerResponseText}
                    onChange={(e) => setBorrowerResponseText(e.target.value)}
                    disabled={!responseType}
                    placeholder={responseType ? "Enter your response..." : "Select a response type first"}
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Attach Evidence (Optional)</label>
                  <div className="border border-dashed border-slate-600 rounded-lg p-4 hover:bg-slate-700/30 transition-colors flex items-center justify-center">
                    <input 
                      type="file" 
                      id="borrower-evidence"
                      className="hidden" 
                      accept="image/*,.pdf"
                      onChange={handleBorrowerFileChange}
                      disabled={!responseType || uploadingFiles}
                    />
                    <label 
                      htmlFor="borrower-evidence" 
                      className={`cursor-pointer flex items-center gap-2 ${
                        !responseType || uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploadingFiles ? (
                        <>
                          <Loader2 className="text-emerald-500 animate-spin" size={20} />
                          <span className="text-sm text-slate-400">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="text-emerald-500" size={20} />
                          <span className="text-sm text-slate-400">Upload Image or PDF</span>
                        </>
                      )}
                    </label>
                  </div>

                  {borrowerAttachments.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {borrowerAttachments.map((file) => (
                        <div key={file.id} className="relative group bg-slate-900 rounded border border-slate-700 p-1 hover:border-slate-600 transition-colors">
                          <button
                            type="button"
                            onClick={() => removeBorrowerAttachment(file.id)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow hover:bg-red-600 transition-colors z-10"
                          >
                            <X size={10} />
                          </button>
                          <div className="h-12 w-full rounded overflow-hidden flex items-center justify-center bg-black/50">
                            {file.type.startsWith('image/') ? (
                              <img src={file.data} alt={file.name} className="h-full w-full object-cover" />
                            ) : (
                              <FileText className="text-slate-400" size={16} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <button 
                    onClick={submitBorrowerResponse}
                    disabled={!borrowerResponseText.trim() || !responseType || updating || uploadingFiles}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-900/20 flex items-center gap-2"
                  >
                    {updating ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Submitting...
                      </>
                    ) : (
                      'Post Response'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {!isSimulatingBorrower && isOwner && (
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl">
              <h3 className="text-white font-bold mb-4">Lender Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => handleStatusChange(ReportStatus.PUBLISHED_SETTLED)}
                  disabled={updating}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-900/50 transition-colors disabled:opacity-50"
                >
                  <CheckCircle size={18} />
                  Mark as Settled
                </button>
                <button 
                  onClick={() => handleStatusChange(ReportStatus.WITHDRAWN)}
                  disabled={updating}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  <Ban size={18} />
                  Withdraw Report
                </button>
                {report.status === ReportStatus.PENDING_RESPONSE && (
                  <>
                    <button 
                      onClick={() => navigate(`/list/#/edit/${report.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-900/20 text-blue-400 border border-blue-500/20 hover:bg-blue-900/30 transition-colors"
                    >
                      <Edit2 size={18} />
                      Edit Report
                    </button>
                    <button 
                      onClick={() => setShowResendModal(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-indigo-900/20 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-900/30 transition-colors"
                    >
                      <Bell size={18} />
                      Resend Notification
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 shadow-xl">
            <h3 className="text-slate-400 text-sm font-bold uppercase mb-4">Timeline</h3>
            <div className="space-y-4 relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-slate-800"></div>
              
              <div className="relative pl-6">
                <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-slate-600 border-4 border-slate-900"></div>
                <p className="text-xs text-slate-500">
                  {report.createdDate?.seconds 
                    ? new Date(report.createdDate.seconds * 1000).toLocaleDateString()
                    : new Date(report.createdDate).toLocaleDateString()
                  }
                </p>
                <p className="text-sm text-slate-300">Report Created</p>
              </div>
              
              {report.borrowerResponse && (
                <div className="relative pl-6">
                  <div className={`absolute left-0 top-1 w-4 h-4 rounded-full border-4 border-slate-900 ${
                    report.borrowerResponseType === BorrowerResponseType.DISPUTE ? 'bg-orange-500' :
                    report.borrowerResponseType === BorrowerResponseType.CLAIM_PAID ? 'bg-emerald-500' :
                    'bg-blue-500'
                  }`}></div>
                  <p className="text-xs text-slate-500">{report.borrowerResponseDate}</p>
                  <p className="text-sm text-slate-300">
                    Response: {report.borrowerResponseType ? report.borrowerResponseType.replace(/_/g, ' ') : 'General Reply'}
                  </p>
                </div>
              )}
              
              {report.status === ReportStatus.PUBLISHED_SETTLED && (
                <div className="relative pl-6">
                  <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-emerald-500 border-4 border-slate-900"></div>
                  <p className="text-xs text-slate-500">Recent</p>
                  <p className="text-sm text-emerald-400">Settled</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MessageSenderModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        report={report}
        onMessageSent={() => {
          setShowMessageModal(false);
          loadReport();
        }}
      />
    </div>
  );
};

export default ManageDefaulter;